import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Heart, ShoppingBag, Sparkles, Check, Copy } from "lucide-react";
import { useWishlist } from "@/context/WishlistContext";
import { useCart } from "@/context/CartContext";
import { useI18n } from "@/context/I18nContext";

const PROMO_CODE = "RIZK10";
const SESSION_KEY = "makerizk_promo_shown_v1";
const DISMISS_KEY = "makerizk_promo_dismissed_v1";
const DELAY_MS = 25000;

export default function WishlistPromoPopup() {
    const { count: wishCount } = useWishlist();
    const { count: cartCount } = useCart();
    const { t } = useI18n();
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        // Skip if already dismissed for good, or already shown this session
        if (sessionStorage.getItem(SESSION_KEY)) return;
        if (localStorage.getItem(DISMISS_KEY)) return;

        let triggered = false;
        const trigger = () => {
            if (triggered) return;
            // Only show if user has something to convert
            if (wishCount === 0 && cartCount === 0) return;
            triggered = true;
            setOpen(true);
            sessionStorage.setItem(SESSION_KEY, "1");
        };

        // Time-based trigger
        const timeoutId = setTimeout(trigger, DELAY_MS);

        // Exit-intent (desktop only, mouse leaves top of viewport)
        const onMouseLeave = (e) => {
            if (e.clientY <= 0) trigger();
        };
        if (window.matchMedia("(min-width: 768px)").matches) {
            document.addEventListener("mouseleave", onMouseLeave);
        }

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener("mouseleave", onMouseLeave);
        };
    }, [wishCount, cartCount]);

    const handleClose = () => setOpen(false);

    const handleDismissForever = () => {
        localStorage.setItem(DISMISS_KEY, "1");
        setOpen(false);
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(PROMO_CODE);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch (_) {}
    };

    if (!open) return null;

    const primaryCta = cartCount > 0
        ? { to: "/checkout", label: t("promo.cta_cart") }
        : { to: "/wishlist", label: t("promo.cta_wishlist") };

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-in fade-in duration-300"
            data-testid="promo-popup"
            role="dialog"
            aria-modal="true"
        >
            <div
                className="relative w-full max-w-md bg-[#0a0a0a] border border-white/15 p-8 sm:p-10 text-center"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={handleClose}
                    className="absolute top-4 end-4 text-white/60 hover:text-white transition-colors"
                    aria-label={t("nav.close")}
                    data-testid="promo-close-btn"
                >
                    <X size={20} strokeWidth={1.5} />
                </button>

                <div className="flex items-center justify-center gap-2 text-xs uppercase tracking-[0.4em] text-white/50 mb-6">
                    <Sparkles size={14} strokeWidth={1.5} />
                    {t("promo.eyebrow")}
                </div>

                <h2 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter leading-[0.95]">
                    {t("promo.title_1")}<br />
                    <span className="text-white/60">{t("promo.title_2")}</span>
                </h2>

                <p className="mt-6 text-sm text-white/70 leading-relaxed">
                    {t("promo.desc")}
                </p>

                {/* Code block */}
                <div className="mt-8 border border-dashed border-white/30 p-5">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">
                        {t("promo.your_code")}
                    </p>
                    <p
                        className="font-display text-3xl sm:text-4xl font-black tracking-[0.15em]"
                        data-testid="promo-code-display"
                    >
                        {PROMO_CODE}
                    </p>
                    <button
                        onClick={handleCopy}
                        className={`mt-4 inline-flex items-center gap-2 h-10 px-4 text-xs uppercase tracking-[0.2em] font-bold border transition-colors ${
                            copied
                                ? "bg-white text-black border-white"
                                : "border-white/30 hover:bg-white hover:text-black"
                        }`}
                        data-testid="promo-copy-btn"
                    >
                        {copied ? (
                            <>
                                <Check size={13} /> {t("promo.copied")}
                            </>
                        ) : (
                            <>
                                <Copy size={13} /> {t("promo.copy")}
                            </>
                        )}
                    </button>
                </div>

                <Link
                    to={primaryCta.to}
                    onClick={handleClose}
                    className="mt-8 flex items-center justify-center gap-2 w-full h-14 bg-white text-black text-xs uppercase tracking-[0.25em] font-bold hover:bg-white/90 transition-colors"
                    data-testid="promo-cta-btn"
                >
                    {cartCount > 0 ? (
                        <ShoppingBag size={14} strokeWidth={1.5} />
                    ) : (
                        <Heart size={14} strokeWidth={1.5} />
                    )}
                    {primaryCta.label}
                </Link>

                <button
                    onClick={handleDismissForever}
                    className="mt-4 text-xs uppercase tracking-[0.2em] text-white/40 hover:text-white transition-colors"
                    data-testid="promo-dismiss-forever-btn"
                >
                    {t("promo.dismiss")}
                </button>

                <p className="mt-4 text-[10px] uppercase tracking-[0.3em] text-white/30">
                    {t("promo.expires")}
                </p>
            </div>
        </div>
    );
}
